import React, { useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getPostBySlug } from '../blog/posts';
import { renderPostBody, postText } from '../blog/renderPost';
import { useSeo } from '../seo/useSeo';
import { track } from '../analytics/track';

const SITE = 'https://www.discoduckevents.com';

// Dates are stored as YYYY-MM-DD; format in UTC so a US timezone doesn't show the day before.
export function formatPostDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);

  const bodyHtml = useMemo(() => (post ? renderPostBody(post) : ''), [post]);
  const minutes = useMemo(() => {
    const words = post ? postText(post).split(/\s+/).filter(Boolean).length : 0;
    return Math.max(1, Math.round(words / 200));
  }, [post]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Read depth: record each milestone once per post, measured against the article itself.
  const articleRef = useRef(null);
  useEffect(() => {
    if (!post) return undefined;
    const reached = new Set();
    let frame = 0;
    const measure = () => {
      frame = 0;
      const el = articleRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const seen = (window.innerHeight - rect.top) / rect.height;
      [25, 50, 75, 100].forEach((pct) => {
        if (seen * 100 >= pct && !reached.has(pct)) {
          reached.add(pct);
          track('blog_read_depth', { slug: post.slug, percent: pct });
        }
      });
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [post]);

  useSeo(
    post
      ? {
          title: `${post.title} | Disco Duck Events`,
          description: post.description,
          canonicalPath: `/blog/${post.slug}`,
          type: 'article',
          image: post.image,
          imageAlt: post.imageAlt,
          jsonLd: [
            {
              '@context': 'https://schema.org',
              '@type': 'BlogPosting',
              headline: post.title,
              description: post.description,
              image: [`${SITE}${post.image}`],
              author: { '@type': 'Organization', name: post.author || 'Disco Duck Events' },
              publisher: {
                '@type': 'Organization',
                name: 'Disco Duck Events',
                logo: { '@type': 'ImageObject', url: `${SITE}/DiscoDuckLogo.png` },
              },
              datePublished: post.publishedAt,
              dateModified: post.updatedAt || post.publishedAt,
              mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE}/blog/${post.slug}` },
            },
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
                { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE}/blog` },
                { '@type': 'ListItem', position: 3, name: post.title, item: `${SITE}/blog/${post.slug}` },
              ],
            },
          ],
        }
      : {
          title: 'Blog Post Not Found | Disco Duck Events',
          description: 'The article you’re looking for doesn’t exist (or was moved).',
          canonicalPath: '/blog',
          noIndex: true,
        }
  );

  // The body is pre-rendered HTML, so its links are plain <a> tags. Route internal
  // page links through React Router (no full reload) and offset in-page jumps for
  // the fixed navbar. Files like the PDF checklist are left to the browser.
  const handleBodyClick = (e) => {
    const a = e.target.closest('a');
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    const href = a.getAttribute('href') || '';
    if (href.startsWith('#')) {
      const target = document.getElementById(href.slice(1));
      if (target) {
        e.preventDefault();
        const nav = document.querySelector('.navbar');
        const top = target.getBoundingClientRect().top + window.scrollY - (nav ? nav.offsetHeight : 80) - 16;
        window.scrollTo({ top, behavior: 'smooth' });
        window.history.replaceState(null, '', href);
      }
    } else if (href.startsWith('/') && !/\.[a-z0-9]+$/i.test(href)) {
      e.preventDefault();
      navigate(href);
    }
  };

  if (!post) {
    return (
      <div className="blog-page">
        <header className="blog-header">
          <h1>Post not found</h1>
          <p className="blog-header-intro">The article you&rsquo;re looking for doesn&rsquo;t exist (or was moved).</p>
          <Link className="offering-feature-btn" to="/blog">&larr; Back to the blog</Link>
        </header>
      </div>
    );
  }

  return (
    <div className="blog-page">
      <header className="blog-header blog-header--post">
        <nav className="blog-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true">/</span>
          <Link to="/blog">Blog</Link>
        </nav>
        <p className="offering-feature-eyebrow">{post.category}</p>
        <h1>{post.title}</h1>
        <p className="blog-byline">
          <time dateTime={post.publishedAt}>{formatPostDate(post.publishedAt)}</time>
          <span aria-hidden="true">&middot;</span>
          <span>{minutes} min read</span>
        </p>
      </header>

      {post.image && (
        <figure className="blog-hero-image">
          <img src={post.image} alt={post.imageAlt || ''} width="1600" height="1067" />
        </figure>
      )}

      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <article
        ref={articleRef}
        className="blog-article"
        onClick={handleBodyClick}
        dangerouslySetInnerHTML={{ __html: bodyHtml }}
      />

      <div className="blog-post-footer">
        <Link className="blog-backlink" to="/blog">&larr; More from the blog</Link>
      </div>
    </div>
  );
};

export default BlogPost;
